// Generate a dynamic color palette based on the number of movements needed
export const generateDynamicColorPalette = (numMovements: number): string[] => {
  if (numMovements === 0) return [];

  // Helper function to convert HSL to hex
  const hslToHex = (
    hue: number,
    saturation: number,
    lightness: number,
  ): string => {
    const h = hue / 360;
    const s = saturation / 100;
    const l = lightness / 100;

    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h * 6) % 2) - 1));
    const m = l - c / 2;

    let r, g, b;
    if (h < 1 / 6) {
      r = c;
      g = x;
      b = 0;
    } else if (h < 2 / 6) {
      r = x;
      g = c;
      b = 0;
    } else if (h < 3 / 6) {
      r = 0;
      g = c;
      b = x;
    } else if (h < 4 / 6) {
      r = 0;
      g = x;
      b = c;
    } else if (h < 5 / 6) {
      r = x;
      g = 0;
      b = c;
    } else {
      r = c;
      g = 0;
      b = x;
    }

    const hexR = Math.round((r + m) * 255)
      .toString(16)
      .padStart(2, "0");
    const hexG = Math.round((g + m) * 255)
      .toString(16)
      .padStart(2, "0");
    const hexB = Math.round((b + m) * 255)
      .toString(16)
      .padStart(2, "0");

    return `#${hexR}${hexG}${hexB}`;
  };

  // Helper function to get color family from hue (group similar hues together)
  const getColorFamily = (hue: number): number => {
    // Group hues into 6 main color families with some overlap tolerance
    // Red: 0-30, 330-360
    // Orange: 15-45
    // Yellow: 30-60
    // Green: 60-150
    // Blue: 150-240
    // Purple: 240-330
    if (hue >= 330 || hue < 30) return 0; // Red
    if (hue >= 30 && hue < 60) return 1; // Yellow
    if (hue >= 60 && hue < 150) return 2; // Green
    if (hue >= 150 && hue < 240) return 3; // Blue
    return 4; // Purple (240-330)
  };

  // Helper function to check if two hues are in the same color family
  const areSimilarColors = (hue1: number, hue2: number): boolean => {
    const family1 = getColorFamily(hue1);
    const family2 = getColorFamily(hue2);
    return family1 === family2;
  };

  // Generate initial colors with good distribution
  const colorData: Array<{
    hue: number;
    saturation: number;
    lightness: number;
  }> = [];
  for (let i = 0; i < numMovements; i++) {
    const hue = (i * 137.5) % 360; // Golden angle for good distribution
    const saturation = 70 + (i % 3) * 10; // Vary saturation between 70-90%
    const lightness = 50 + (i % 2) * 10; // Vary lightness between 50-60%
    colorData.push({ hue, saturation, lightness });
  }

  // Reorder colors to ensure no adjacent similar colors
  const reordered: Array<{
    hue: number;
    saturation: number;
    lightness: number;
  }> = [];
  const used = new Set<number>();

  // Start with the first color
  if (colorData.length > 0) {
    reordered.push(colorData[0]);
    used.add(0);
  }

  // Greedy algorithm: for each position, pick the color that's least similar to the previous one
  while (reordered.length < numMovements) {
    const lastHue = reordered[reordered.length - 1].hue;
    let bestIndex = -1;
    let bestScore = -1;

    // Find the unused color that's most different from the last one
    for (let i = 0; i < colorData.length; i++) {
      if (used.has(i)) continue;

      const candidateHue = colorData[i].hue;
      const isSimilar = areSimilarColors(lastHue, candidateHue);

      // Calculate hue distance (considering circular nature)
      const hueDiff = Math.min(
        Math.abs(candidateHue - lastHue),
        360 - Math.abs(candidateHue - lastHue),
      );

      // Score: prefer colors that are different families and far apart in hue
      const score = isSimilar ? -1 : hueDiff;

      if (score > bestScore) {
        bestScore = score;
        bestIndex = i;
      }
    }

    // If we found a good candidate, use it
    if (bestIndex !== -1) {
      reordered.push(colorData[bestIndex]);
      used.add(bestIndex);
    } else {
      // Fallback: use any remaining color
      for (let i = 0; i < colorData.length; i++) {
        if (!used.has(i)) {
          reordered.push(colorData[i]);
          used.add(i);
          break;
        }
      }
    }
  }

  // Convert to hex colors
  return reordered.map(({ hue, saturation, lightness }) =>
    hslToHex(hue, saturation, lightness),
  );
};
